import { showError } from '@calendly/alert'
import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { useDispatch } from 'react-redux'

import { useETEditorFormikPoolingType } from 'services/event_types'

import { filterByPoolUid, findUserPool, removeMembership, removeUserPool, removeUserPoolUids } from '../helpers'
import { useHostsListData } from './use_hosts_list_data'
import { useSetValuesAndSubmit } from './use_set_values_and_submit'
import { useUserPoolsData } from './use_user_pools_data'

export const useRemoveMember = (eventType) => {
  const dispatch = useDispatch()
  const isNewEventType = !eventType?.id

  const { values: { memberships } } = useFormContext()
  const setValues = useSetValuesAndSubmit({ withSubmit: !isNewEventType })

  const { isTeam } = eventType
  const { isCollectiveRoundRobin } = useETEditorFormikPoolingType()

  const {
    userPools,
    collectiveUserPoolsCount,
    roundRobinUserPoolsCount,
    isLastUserPool,
  } = useUserPoolsData()

  const { hosts, isLastHost } = useHostsListData(null, isTeam)

  return (eventTypeMembership, userPoolUid) => {
    const isLastUserPoolHost = filterByPoolUid(hosts, userPoolUid).length === 1
    const userPool = findUserPool(userPools, userPoolUid)

    const updatedMemberships = removeMembership({
      memberships,
      eventTypeMembership,
      markForDestruction: !isTeam
    })

    const showLastMembershipError = isCollectiveRoundRobin
      ? isLastUserPool && isLastUserPoolHost
      : isLastHost

    if (showLastMembershipError) {
      return dispatch(showError(
        I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.can_not_remove_last_membership')
      ))
    }

    // When it's not Collective Round Robin
    // Or it's not the last user pool membership,
    // Then we remove the membership
    if (!isCollectiveRoundRobin || !isLastUserPoolHost) {
      return setValues((values) => ({
        ...values,
        memberships: updatedMemberships
      }))
    }

    // When it's the last membership in the user pool
    // And we remove it for new ET,
    // Then we check if we need to convert Collective Round Robin
    if (isNewEventType) {
      const shouldConvertToCollective = (
        userPool.pooling_type === 'round_robin'
        && collectiveUserPoolsCount === 1
        && roundRobinUserPoolsCount === 1
      )
      const shouldConvertToRoundRobin = (
        userPool.pooling_type === 'round_robin'
        && collectiveUserPoolsCount === 0
        && roundRobinUserPoolsCount === 2
      ) || (
        userPool.pooling_type === 'collective'
        && collectiveUserPoolsCount === 1
        && roundRobinUserPoolsCount === 1
      )

      if (shouldConvertToCollective || shouldConvertToRoundRobin) {
        return setValues((values) => ({
          ...values,
          allocationPreference: 'availability',
          pooling_type: shouldConvertToCollective ? 'collective' : 'round_robin',
          userPools: [],
          memberships: removeUserPoolUids(updatedMemberships)
        }))
      }
    }

    // When a member is the last one in the user pool,
    // Then we remove the user pool as well
    const updatedUserPools = removeUserPool(userPools, userPoolUid)

    return setValues((values) => ({
      ...values,
      userPools: updatedUserPools,
      memberships: updatedMemberships
    }))
  }
}
